import { isPlannable, weekCapacity } from './capacity.js';
import type { CapacityEmployee } from './capacity.js';
import { compareIsoWeeks } from './iso-week.js';
import type { IsoWeek } from './iso-week.js';
import { belastungInWoche } from './project-status.js';
import type { StatusWechsel } from './project-status.js';
import type { ProjectStatus } from './project-fields.js';

/**
 * Belegung ändern und verschieben (AC-403) — die EINE Stelle für die Regeln,
 * die Oberfläche und Server gleich anwenden: was eine Eingabe in der Zelle
 * bedeutet, wann eine Bestätigung fällig ist und welche Woche abgeschlossen ist.
 *
 * Der Server prüft mit denselben Funktionen; die Oberfläche zeigt damit vorab,
 * was der Server sagen würde, und schickt nichts Ungültiges ab (AK 5).
 * Entscheidungen in docs/stories/AC-403/ENTSCHEIDUNGEN.md; die Nummern
 * (E1 …, H1 …) verweisen dorthin.
 *
 * Stunden sind Dezimalzeichenketten und werden als Hundertstel in `bigint`
 * verglichen, nie über `Number` (Kernregel 10). Wochen nur über `iso-week.ts`
 * (Kernregel 9).
 *
 * Transportfrei (ADR-04) und OHNE Zod: die Oberfläche lädt die Datei über
 * `@asas/domain/belegung`. Die Schemata stehen in `planning.ts`.
 */

/**
 * Stunden als Eingabe: Dezimalzeichenkette mit Punkt, höchstens drei Vor- und
 * zwei Nachkommastellen — `NUMERIC(5,2)`. EINE Regel für Belegung und
 * Abwesenheit; exportiert, weil die Excel-Zuordnung (AC-406) damit leere von
 * ungültigen Zellen unterscheidet.
 */
export const STUNDEN_PATTERN = /^\d{1,3}(?:\.\d{1,2})?$/;

/** Hat die Form von `STUNDEN_PATTERN` und ist null („0", „0.00"). */
export function istNullStunden(value: string): boolean {
  return STUNDEN_PATTERN.test(value) && !/[1-9]/.test(value);
}

/**
 * Was eine Eingabe in der Zelle bedeutet (AK 5, E2):
 *
 *   leer       nichts oder null — die Belegung entfällt (eine leere oder
 *              Null-Zelle ist keine Zeile, Kernregel 26)
 *   stunden    ein gültiger Wert in Speicherform mit Punkt
 *   ungueltig  negativ, keine Zahl, zu viele Stellen — abgewiesen, der
 *              bisherige Wert bleibt stehen
 */
export type StundenEingabe =
  | { readonly art: 'leer' }
  | { readonly art: 'stunden'; readonly stunden: string }
  | { readonly art: 'ungueltig' };

/**
 * Liest, was in der Zelle steht. Ein Komma gilt als Dezimalzeichen („7,5"),
 * wie in der Bestands-Excel; umgesetzt wird nur das Zeichen, nie über `Number`.
 */
export function leseStundenEingabe(text: string): StundenEingabe {
  const roh = text.trim();

  if (roh === '') return { art: 'leer' };

  const stunden = roh.replace(',', '.');

  if (!STUNDEN_PATTERN.test(stunden)) return { art: 'ungueltig' };
  if (istNullStunden(stunden)) return { art: 'leer' };

  return { art: 'stunden', stunden };
}

/** `'12.5'` → `1250n`. Nur für Zeichenketten in der Form von `STUNDEN_PATTERN` oder gespeicherte Werte. */
function hundertstel(stunden: string): bigint {
  if (!/^\d+(?:\.\d{1,2})?$/.test(stunden)) {
    throw new RangeError(`Keine Stundenangabe: ${stunden}`);
  }
  const [ganz = '0', bruch = ''] = stunden.split('.');
  return BigInt(ganz) * 100n + BigInt(bruch.padEnd(2, '0'));
}

/**
 * Verlangt der Wert eine ausdrückliche Bestätigung (AK 6, H4)? MEHR als die
 * Schwelle in ganzen Stunden — für den eingegebenen Wert der einen Belegung,
 * nicht für die Wochensumme der Person: ein Schutz vor Tippfehlern, keine
 * Überlastprüfung (die ist AK 4). Die Schwelle steht je Mandant in
 * `tenant_settings.thresholds.belegungBestaetigungUeberStunden`.
 */
export function brauchtStundenBestaetigung(stunden: string, schwelleStunden: number): boolean {
  return hundertstel(stunden) > BigInt(schwelleStunden) * 100n;
}

/**
 * Ist die Woche abgeschlossen (AK 7, E4)? Jede Woche vor der laufenden
 * ISO-Woche in `FACHLICHE_ZEITZONE` — dieselbe Grenze wie bei Stammdaten
 * (Kernregel 22). Die laufende Woche gibt der Aufrufer, damit „heute" an einer
 * Stelle bestimmt wird (`currentIsoWeek(new Date(), FACHLICHE_ZEITZONE)`).
 */
export function istAbgeschlosseneWoche(woche: IsoWeek, laufendeWoche: IsoWeek): boolean {
  return compareIsoWeeks(woche, laufendeWoche) < 0;
}

/**
 * Berührt ein Schreibweg eine abgeschlossene Woche (E4)? Beim Verschieben die
 * Quell- UND die Zielwoche, sonst die Woche der Belegung. Dann verlangt er
 * `resource_allocation:edit_past` und eine Begründung (AK 7).
 */
export function beruehrtAbgeschlosseneWoche(
  wochen: readonly IsoWeek[],
  laufendeWoche: IsoWeek,
): boolean {
  return wochen.some((woche) => istAbgeschlosseneWoche(woche, laufendeWoche));
}

/** Höchstlänge der Begründung eines Nachtrags (AK 7). */
export const NACHTRAG_BEGRUENDUNG_MAX_LENGTH = 1000;

/**
 * Was ein Nachtrag in einer abgeschlossenen Woche getan hat (E5). Die Werte vor
 * und nach der Änderung stehen im Audit-Protokoll (AC-009); der Nachtrag hält
 * die Begründung fest.
 */
export const NACHTRAG_AKTIONEN = ['angelegt', 'geaendert', 'verschoben', 'geloescht'] as const;
export type NachtragAktion = (typeof NACHTRAG_AKTIONEN)[number];

/**
 * Warum eine Belegung in einer Woche nicht entstehen darf (Anlegen, Ziel einer
 * Verschiebung) — dieselben Gründe wie bei der Freigabe einer Anfrage (AC-405):
 *
 *   nicht_planbar       extern oder Stammsatz unvollständig (`isPlannable`)
 *   nicht_beschaeftigt  die Woche liegt vor dem Eintritt oder nach dem Austritt
 *   ohne_wirkung        das Projekt ist in der Woche abgeschlossen, verloren
 *                       oder archiviert (`belastungInWoche`, Kernregel 25)
 *
 * Eine andere Fachgruppe ist KEIN Grund: geplant wird bis AC-806 in allen
 * Fachgruppen (H2).
 */
export type BelegungsZielBefund = 'nicht_planbar' | 'nicht_beschaeftigt' | 'ohne_wirkung';

export function pruefeBelegungsZiel(args: {
  readonly person: CapacityEmployee;
  readonly projekt: { readonly aktuell: ProjectStatus; readonly wechsel: readonly StatusWechsel[] };
  readonly woche: IsoWeek;
}): BelegungsZielBefund | null {
  if (!isPlannable(args.person)) return 'nicht_planbar';
  if (!weekCapacity(args.person, args.woche).planbar) return 'nicht_beschaeftigt';
  if (belastungInWoche(args.projekt.aktuell, args.projekt.wechsel, args.woche) === 'keine') {
    return 'ohne_wirkung';
  }
  return null;
}
